import type { ClinicAvailability } from '../shared/clinic/availability';
import type { CampaignEvent, MemberCampaign } from '../shared/campaigns';
import type { Tournament } from '../shared/tournaments';
import type {
  ApprovalEmailPreview,
  Decision,
  ReviewKind,
  TeamReview,
} from '../shared/teamReview';
import type {
  EventDetailsDraft,
  EventFormSave,
  EventStage,
  EventHistoryEntry,
  EventTeamSummary,
  ManagedEvent,
  MemberEventResponse,
  PortalEvent,
} from '../shared/events';
import type {
  TournamentForm,
  QuestionnaireCommand,
  QuestionnaireProfile,
  QuestionnaireResponseReport,
  QuestionnaireSubject,
  QuestionnaireSubmission,
  TournamentQuestionnaireSubject,
  TournamentQuestionnaireView,
} from '../shared/questionnaires';
import type { Upcoming } from '../shared/upcoming';
import type { Gate } from '../shared/gates';
import type { Application as ClinicApplication } from '../shared/clinic/application';
import { recordEvent, type Diagnostics } from './diagnostics';
import { parsePath } from '../shared/pages';
import type { Level } from '../shared/levels';
import type {
  AdminDropinEntry,
  AdminDropinRemovalResult,
  AdminDropinRefundResult,
  AttendanceChange,
  AttendanceResult,
  AttendanceSheet,
  DeclaredSubs,
  DropinCancellationResult,
  DropinCheckout,
  DropinEntry,
  DropinHistoryEntry,
  Gender,
  IssueReportKind,
  MemberReportKind,
  LevelOverrideResult,
  MemberSummary,
  MemberClinics,
  MemberLeague,
  MemberRequirements,
  PortalData,
  TeamDetail,
  TeamSubs,
  WaiverEntry,
} from '../shared/contracts';
import type { WaiverDocumentId } from '../shared/waivers';
import type {
  DirectoryMember,
  TeamRegistrationSubmission,
  TeamRegistrationView,
} from '../shared/teamRegistration';

export type ApiFailureKind =
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'not-found'
  | 'invalid'
  | 'rate-limited'
  | 'server'
  | 'unexpected';

export interface ApiFailure {
  kind: ApiFailureKind;
  status?: number;
  message?: string;
  reason?: string;
  retryAfterSeconds?: number;
  body?: Record<string, unknown>;
}

export class ApiError extends Error {
  constructor(readonly failure: ApiFailure) {
    super(failure.message ?? failure.kind);
    this.name = 'ApiError';
  }
}

export function failureKindOf(error: unknown): ApiFailureKind | null {
  return error instanceof ApiError ? error.failure.kind : null;
}

export function failureReasonOf(error: unknown): string | undefined {
  return error instanceof ApiError ? error.failure.reason : undefined;
}

export function failureMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError
    ? (error.failure.message ?? fallback)
    : fallback;
}

function failureKind(status: number): ApiFailureKind {
  if (status === 400 || status === 422) return 'invalid';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not-found';
  if (status === 429) return 'rate-limited';
  if (status >= 500) return 'server';
  return 'unexpected';
}

function retryAfterSeconds(response: Response): number | undefined {
  const value = response.headers.get('Retry-After');
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return Math.ceil(seconds);
  const date = Date.parse(value);
  if (!Number.isFinite(date)) return undefined;
  return Math.max(0, Math.ceil((date - Date.now()) / 1000));
}

async function responseFailure(response: Response): Promise<{
  message?: string;
  reason?: string;
  body?: Record<string, unknown>;
}> {
  try {
    const body = (await response.json()) as unknown;
    if (typeof body !== 'object' || body === null) return {};
    const { error, reason } = body as { error?: unknown; reason?: unknown };
    return {
      ...(typeof error === 'string' ? { message: error } : {}),
      ...(typeof reason === 'string' ? { reason } : {}),
      body: body as Record<string, unknown>,
    };
  } catch {
    return {};
  }
}

const SECTION_HEADER = 'X-Portal-Section';

function sectionHint(path: string): string {
  const { section } = parsePath(path);
  return section === 'not-found' ? 'unknown' : section;
}

function withSection(init?: RequestInit): RequestInit {
  const location = globalThis.location as Location | undefined;
  if (location === undefined) return init ?? {};
  const path = location.pathname;
  const headers = new Headers(init?.headers);
  headers.set(SECTION_HEADER, sectionHint(path));
  return { ...init, headers };
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  let response: Response;
  try {
    response = await fetch(path, withSection(init));
  } catch {
    recordEvent(`${method} ${path} → could not reach the portal`);
    throw new ApiError({ kind: 'network' });
  }

  if (!response.ok) {
    recordEvent(`${method} ${path} → ${response.status}`);
    throw new ApiError({
      kind: failureKind(response.status),
      status: response.status,
      ...(await responseFailure(response)),
      retryAfterSeconds: retryAfterSeconds(response),
    });
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError({ kind: 'server', status: response.status });
  }
}

function postJson<T = unknown>(path: string, body?: unknown): Promise<T> {
  return requestJson<T>(path, {
    method: 'POST',
    ...(body === undefined
      ? {}
      : {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
  });
}

type ClinicApplicationResponse = { application: ClinicApplication };
export type ClinicStartMode = 'start' | 'restart' | 'redo';

/** Starts or resumes this member's clinic application, restarts it as a new member, or reopens a submitted one. */
export async function startClinicApplication(
  mode: ClinicStartMode,
): Promise<ClinicApplication> {
  const path = `/api/clinic-application/${mode}`;
  return (await postJson<ClinicApplicationResponse>(path, {})).application;
}

export const fetchGate = (gate: Gate) =>
  requestJson<{ unlocked: boolean }>(`/api/gate/${gate}`);

export const unlockGate = (gate: Gate, password: string) =>
  postJson<{ unlocked: true }>(`/api/gate/${gate}`, { password });

export const fetchClinicAvailability = () =>
  requestJson<ClinicAvailability>('/api/clinic-application/availability');

export async function saveClinicApplication(command: {
  availability?: string;
  version: number;
  step: ClinicApplication['step'];
  answers: ClinicApplication['answers'];
  submit: boolean;
}): Promise<ClinicApplication> {
  return (
    await postJson<ClinicApplicationResponse>(
      '/api/clinic-application/save',
      command,
    )
  ).application;
}

export const fetchMe = () => requestJson<PortalData>('/api/me');

export const fetchMemberLeague = () =>
  requestJson<MemberLeague>('/api/me/league');

export const fetchMemberClinics = () =>
  requestJson<MemberClinics>('/api/me/clinics');

export const fetchMemberRequirements = () =>
  requestJson<MemberRequirements>('/api/me/requirements');

export const fetchDeclaredSubs = (season: string) =>
  requestJson<DeclaredSubs>(`/api/league/subs/${encodeURIComponent(season)}`);

export const fetchTeam = (teamId: string) =>
  requestJson<TeamDetail>(`/api/me/teams/${encodeURIComponent(teamId)}`);

export const addTeamSub = (teamId: string, email: string) =>
  postJson<TeamSubs>(`/api/me/teams/${encodeURIComponent(teamId)}/subs`, {
    email,
  });

export const removeTeamSub = (teamId: string, memberId: string) =>
  requestJson<TeamSubs>(
    `/api/me/teams/${encodeURIComponent(teamId)}/subs/${encodeURIComponent(memberId)}`,
    { method: 'DELETE' },
  );

export const submitTeamSubs = (teamId: string) =>
  postJson<TeamSubs>(`/api/me/teams/${encodeURIComponent(teamId)}/subs/submit`);

export const fetchRegistration = () =>
  requestJson<TeamRegistrationView>('/api/me/registration');

export const lookUpRegistrationPlayer = (email: string) =>
  postJson<DirectoryMember>('/api/me/registration/players', { email });

export const registerTeam = (submission: TeamRegistrationSubmission) =>
  postJson<TeamRegistrationView>('/api/me/registration', submission);

export const updateRegistration = (
  teamId: string,
  submission: TeamRegistrationSubmission,
) =>
  requestJson<TeamRegistrationView>(
    `/api/me/registration/${encodeURIComponent(teamId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(submission),
    },
  );

export const leaveRegistration = (teamId: string) =>
  requestJson<TeamRegistrationView>(
    `/api/me/registration/${encodeURIComponent(teamId)}`,
    { method: 'DELETE' },
  );

export const fetchDropins = () => requestJson<DropinEntry[]>('/api/dropins');

export const fetchDropinHistory = () =>
  requestJson<DropinHistoryEntry[]>('/api/dropins/history');

export const fetchWaivers = () => requestJson<WaiverEntry[]>('/api/me/waivers');

export const signWaiver = (document: WaiverDocumentId) =>
  postJson<{ ok: true; waiverId: string }>('/api/me/waivers', { document });

export const bookDropin = (id: string) =>
  postJson<DropinCheckout>(
    `/api/dropins/${encodeURIComponent(id)}/checkout`,
    {},
  );

export const cancelDropinBooking = (id: string) =>
  postJson<DropinCancellationResult>(
    `/api/dropins/${encodeURIComponent(id)}/cancel`,
    {},
  );

export const fetchAdminDropins = () =>
  requestJson<AdminDropinEntry[]>('/api/admin/dropins');

export const refundDropinBooking = (dropinId: string, reservationId: string) =>
  postJson<AdminDropinRefundResult>(
    `/api/admin/dropins/${encodeURIComponent(dropinId)}/bookings/${encodeURIComponent(reservationId)}/refund`,
    {},
  );

export const removeDropinBooking = (dropinId: string, reservationId: string) =>
  postJson<AdminDropinRemovalResult>(
    `/api/admin/dropins/${encodeURIComponent(dropinId)}/bookings/${encodeURIComponent(reservationId)}/remove`,
    {},
  );

export const fetchAttendanceSheets = () =>
  requestJson<AttendanceSheet[]>('/api/staff/dropins');

export const saveAttendance = (
  dropinId: string,
  changes: readonly AttendanceChange[],
) =>
  postJson<AttendanceResult>(
    `/api/staff/dropins/${encodeURIComponent(dropinId)}/attendance`,
    { changes },
  );

export const saveLevelOverride = (
  memberId: string,
  levelOverride: Level | '',
) =>
  postJson<LevelOverrideResult>(
    `/api/staff/members/${encodeURIComponent(memberId)}/level`,
    { levelOverride },
  );

export interface ProfileEdits {
  firstName: string;
  lastName: string;
  preferredName: string;
  phone: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
  gender?: Gender;
}

export const saveProfile = (edits: Partial<ProfileEdits>) =>
  requestJson<unknown>('/api/me/profile', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(edits),
  });

export const fileIssueReport = (report: {
  kind: MemberReportKind;
  description: string;
  context: Diagnostics;
}) => postJson<unknown>('/api/issue-reports', report);

export const reportSub = (report: {
  memberId: string;
  teamId: string;
  description: string;
}) =>
  postJson<unknown>('/api/issue-reports', {
    kind: 'sub-eligibility' satisfies IssueReportKind,
    ...report,
  });

export function teamLogoUrl(teamId: string, version?: string): string {
  const path = `/api/teams/${encodeURIComponent(teamId)}/logo`;
  return version === undefined ? path : `${path}?v=${version}`;
}

export const uploadTeamLogo = (teamId: string, image: Blob) =>
  requestJson<{ version: string }>(
    `/api/me/teams/${encodeURIComponent(teamId)}/logo`,
    {
      method: 'PUT',
      headers: { 'Content-Type': image.type },
      body: image,
    },
  );

export const requestCode = (email: string) =>
  postJson<unknown>('/api/auth/request-code', { email });

export async function verifyLogin(email: string, code: string): Promise<void> {
  try {
    await postJson('/api/auth/verify', { email, code });
  } catch (error) {
    if (failureKindOf(error) === 'unauthorized') {
      throw new ApiError({ ...(error as ApiError).failure, kind: 'invalid' });
    }
    throw error;
  }
}

export const logout = () => postJson<unknown>('/api/auth/logout');

export const searchMembers = (query: string) =>
  requestJson<MemberSummary[]>(
    `/api/admin/members?q=${encodeURIComponent(query)}`,
  );

export const changeMemberEmail = (memberId: string, email: string) =>
  postJson<{ ok: true; memberId: string; requeued: boolean }>(
    `/api/admin/members/${encodeURIComponent(memberId)}/email`,
    { email },
  );

/** The member record that already holds the address a change asked for. */
export function takenBy(error: unknown): MemberSummary | null {
  if (failureReasonOf(error) !== 'taken') return null;
  const other = (error as ApiError).failure.body?.['other'] as
    | Partial<MemberSummary>
    | undefined;
  return typeof other?.memberId === 'string' &&
    typeof other.name === 'string' &&
    typeof other.email === 'string'
    ? { memberId: other.memberId, name: other.name, email: other.email }
    : null;
}

export const mergeMembers = (sourceId: string, destinationId: string) =>
  postJson<{ ok: true; memberId: string; moved: number; requeued: boolean }>(
    `/api/admin/members/${encodeURIComponent(sourceId)}/merge`,
    { into: destinationId },
  );

export const impersonate = (memberId: string) =>
  postJson<unknown>('/api/admin/impersonate', { memberId });

export const stopImpersonating = () =>
  postJson<unknown>('/api/admin/stop-impersonating');

export const fetchTournaments = () =>
  requestJson<Tournament[]>('/api/tournaments');
export const fetchEvents = () => requestJson<PortalEvent[]>('/api/events');
export const fetchUpcoming = () => requestJson<Upcoming>('/api/me/upcoming');
export const registerForEvent = (
  id: string,
  teamId?: string,
  questionnaire?: QuestionnaireSubmission,
) =>
  postJson<PortalEvent>(`/api/events/${encodeURIComponent(id)}/registration`, {
    ...(teamId ? { teamId } : {}),
    ...(questionnaire ? { questionnaire } : {}),
  });
export const editEventAnswers = (
  id: string,
  questionnaire: QuestionnaireSubmission,
) =>
  postJson<PortalEvent>(
    `/api/events/${encodeURIComponent(id)}/registration/answers`,
    { questionnaire },
  );
export const fetchEventResponse = (id: string) =>
  requestJson<MemberEventResponse | null>(
    `/api/events/${encodeURIComponent(id)}/response`,
  );
export const cancelEventRsvp = (id: string) =>
  postJson<PortalEvent>(
    `/api/events/${encodeURIComponent(id)}/registration/cancel`,
  );
export const fetchTournamentRegistration = (id: string) =>
  requestJson<TeamRegistrationView>(
    `/api/tournaments/${encodeURIComponent(id)}/registration`,
  );
export const lookUpTournamentPlayer = (id: string, email: string) =>
  postJson<DirectoryMember>(
    `/api/tournaments/${encodeURIComponent(id)}/players`,
    { email },
  );
export const saveTournamentRegistration = (
  id: string,
  teamId: string | null,
  submission: TeamRegistrationSubmission,
) =>
  requestJson<TeamRegistrationView>(
    `/api/tournaments/${encodeURIComponent(id)}/registration${teamId ? `/${encodeURIComponent(teamId)}` : ''}`,
    {
      method: teamId ? 'PUT' : 'POST',
      body: JSON.stringify(submission),
      headers: { 'Content-Type': 'application/json' },
    },
  );
export const leaveTournamentRegistration = (id: string, teamId: string) =>
  requestJson<TeamRegistrationView>(
    `/api/tournaments/${encodeURIComponent(id)}/registration/${encodeURIComponent(teamId)}`,
    { method: 'DELETE' },
  );
export const moveTournamentRegistration = (
  id: string,
  teamId: string,
  tournamentId: string,
) =>
  postJson<TeamRegistrationView>(
    `/api/tournaments/${encodeURIComponent(id)}/registration/${encodeURIComponent(teamId)}/move`,
    { tournamentId },
  );
export const signTournamentWaiver = (id: string, document: string) =>
  postJson<{ ok: boolean; waiverId: string }>(
    `/api/tournaments/${encodeURIComponent(id)}/waivers`,
    { document },
  );

export const fetchCampaign = () =>
  requestJson<MemberCampaign | null>('/api/me/campaign');
export const recordCampaignProgress = (
  key: string,
  event: Exclude<CampaignEvent, 'completed'>,
  teamId: string,
) =>
  postJson<{ ok: true }>(`/api/me/campaign/${encodeURIComponent(key)}`, {
    event,
    teamId,
  });

export const watchSurveyForm = (formUrl: string, tournamentIds: string[]) =>
  postJson<{ id: string }>('/api/staff/surveys/watch', {
    formUrl,
    tournamentIds,
  });

export const fetchTournamentReview = (tournamentId: string) =>
  requestJson<TeamReview>(
    `/api/staff/tournaments/${encodeURIComponent(tournamentId)}/review`,
  );

/** Null when no season is open for registration. */
export const fetchLeagueReview = () =>
  requestJson<TeamReview | null>('/api/staff/league/review');

const reviewTeamPath = (kind: ReviewKind, teamId: string) =>
  `/api/staff/${kind}-teams/${encodeURIComponent(teamId)}`;

export const decideTeam = (
  kind: ReviewKind,
  teamId: string,
  decision: Decision,
  reason = '',
) =>
  postJson<{ decision: Decision }>(`${reviewTeamPath(kind, teamId)}/decision`, {
    decision,
    reason,
  });

export const fetchApprovalEmail = (kind: ReviewKind, teamId: string) =>
  requestJson<ApprovalEmailPreview>(
    `${reviewTeamPath(kind, teamId)}/approval-email`,
  );

export const sendApprovalEmail = (kind: ReviewKind, teamId: string) =>
  postJson<{ approvalEmailedAt: string }>(
    `${reviewTeamPath(kind, teamId)}/approval-email`,
  );

export const fetchSurveyTournaments = () =>
  requestJson<Pick<Tournament, 'id' | 'name' | 'level'>[]>(
    '/api/staff/surveys/tournaments',
  );

type SubjectRef = Pick<QuestionnaireSubject, 'kind' | 'id'>;
const subjectPath = ({ kind, id }: SubjectRef) =>
  `/api/${kind}-forms/${encodeURIComponent(id)}`;
export const fetchManagedEvents = () =>
  requestJson<ManagedEvent[]>('/api/event-forms');
export const fetchArchivedEvents = () =>
  requestJson<ManagedEvent[]>('/api/event-forms/archived');
export const createManagedEvent = (
  requestId: string,
  details: EventDetailsDraft,
) => postJson<ManagedEvent>('/api/event-forms', { requestId, details });
export const saveManagedEvent = (id: string, save: EventFormSave) =>
  postJson<ManagedEvent>(`/api/event-forms/${encodeURIComponent(id)}`, save);
export const updateManagedEventStage = (id: string, stage: EventStage) =>
  postJson<ManagedEvent>(`/api/event-forms/${encodeURIComponent(id)}/stage`, {
    stage,
  });
export const fetchEventTeams = (id: string) =>
  requestJson<EventTeamSummary[]>(
    `/api/event-forms/${encodeURIComponent(id)}/teams`,
  );
export const fetchEventHistory = (id: string) =>
  requestJson<EventHistoryEntry[]>(
    `/api/event-forms/${encodeURIComponent(id)}/history`,
  );
export const recordEventTeamPayment = (
  id: string,
  teamId: string,
  amountCents: number,
  requestId: string,
) =>
  postJson<EventTeamSummary[]>(
    `/api/event-forms/${encodeURIComponent(id)}/teams/${encodeURIComponent(teamId)}/payments`,
    { amountCents, requestId },
  );
export const fetchEventFormSeasons = () =>
  requestJson<{ id: string; name: string }[]>('/api/event-forms/seasons');
export const fetchEventFormProfile = () =>
  requestJson<QuestionnaireProfile>('/api/event-forms/profile');
export const fetchTournamentForms = () =>
  requestJson<TournamentQuestionnaireSubject[]>('/api/tournament-forms');
export const updateTournamentForm = (
  id: string,
  command: QuestionnaireCommand,
) => postJson<TournamentForm>(subjectPath({ kind: 'tournament', id }), command);
export const fetchQuestionnaireResponses = (subject: SubjectRef) =>
  requestJson<QuestionnaireResponseReport>(`${subjectPath(subject)}/responses`);
export const deleteEventResponse = (id: string, memberId: string) =>
  requestJson<ManagedEvent>(
    `/api/event-forms/${encodeURIComponent(id)}/responses/${encodeURIComponent(memberId)}`,
    { method: 'DELETE' },
  );
export const exportEventResponseSheet = (id: string) =>
  postJson<{ url: string; exportedAt: string }>(
    `/api/event-forms/${encodeURIComponent(id)}/responses/sheet`,
    {},
  );
export const searchEventFormMembers = (query: string) =>
  requestJson<{ id: string; name: string }[]>(
    `/api/event-forms/members?q=${encodeURIComponent(query)}`,
  );
export const fetchQuestionnaireMembers = (subject: SubjectRef) =>
  requestJson<{ id: string; name: string }[]>(
    `${subjectPath(subject)}/members`,
  );

export const fetchTournamentQuestionnaire = (id: string, teamId: string) =>
  requestJson<TournamentQuestionnaireView>(
    `/api/tournaments/${encodeURIComponent(id)}/questionnaire?teamId=${encodeURIComponent(teamId)}`,
  );
export const submitTournamentQuestionnaire = (
  id: string,
  teamId: string,
  submission: QuestionnaireSubmission,
) =>
  postJson<TournamentQuestionnaireView>(
    `/api/tournaments/${encodeURIComponent(id)}/questionnaire?teamId=${encodeURIComponent(teamId)}`,
    submission,
  );
